// ── Enterarse sin estar mirando ──────────────────────────────────────────────
//
// Consulta lo que pasó desde la última vez y, si hay algo, suena una campana y
// —cuando el modo de voz está abierto— lo dice.
//
// **Quién queda afuera lo decide el servidor**, no esto: un evento causado por
// la propia persona no llega hasta acá. La regla vive en un solo lugar
// (services/notifications.ts) porque un filtro repetido en cada pantalla falla
// en una y deja de ser confiable en todas.
//
// Cada 60 segundos y no en vivo, por decisión explícita: para una campana un
// minuto alcanza, y construir un canal de eventos en vivo por esto no se
// justifica (Eduardo, 2026-09-05).

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { sonarCampana } from '../lib/campana.js';
import { defaultTtsProvider } from '../lib/tts.js';
import { useUiPrefs } from './useUiPrefs.js';

export interface Aviso {
  id: number;
  clase: 'email' | 'report' | 'project' | 'task';
  texto: string;
  entityType: string | null;
  entityId: string | null;
  cuando: string;
}

interface Respuesta {
  avisos: Aviso[];
  hasta: string;
}

const CADA = 60_000;

/**
 * Una preferencia apagada es un `'false'` explícito.
 *
 * Ausente significa "nunca la tocó", y ahí el aviso va encendido: la función se
 * pidió para usarla. Solo un apagado deliberado la calla.
 */
function encendido(prefs: Record<string, string> | undefined, clave: string): boolean {
  return prefs?.[clave] !== 'false';
}

export function useAvisos(voz: { activa: boolean }): void {
  const { data: prefs } = useUiPrefs();
  // La marca de corte. Arranca vacía: la primera consulta no trae nada y
  // devuelve el instante actual. Sin eso, abrir la aplicación dispararía una
  // campanada por cada cosa vieja — la forma más rápida de que la apaguen.
  const desde = useRef<string | null>(null);

  const { data } = useQuery<Respuesta>({
    queryKey: ['avisos'],
    queryFn: async () => {
      const q = desde.current ? `?desde=${encodeURIComponent(desde.current)}` : '';
      const r = await api.get<Respuesta>(`/api/notifications${q}`);
      desde.current = r.hasta;
      return r;
    },
    refetchInterval: CADA,
    // Sin reintentos: si falló, en un minuto vuelve a preguntar solo.
    retry: false,
  });

  // Lo ya anunciado, para que un reintento o un remontaje no repita la campana.
  const anunciados = useRef<Set<number>>(new Set());

  useEffect(() => {
    const nuevos = (data?.avisos ?? []).filter(a => !anunciados.current.has(a.id));
    if (nuevos.length === 0) return;
    for (const a of nuevos) anunciados.current.add(a.id);

    if (encendido(prefs, 'aviso.sonido')) sonarCampana();

    // La voz solo cuando el modo de voz está abierto: si no, hablar sin que
    // nadie lo haya pedido asusta más que informa.
    if (voz.activa && encendido(prefs, 'aviso.voz')) {
      // Uno solo aunque hayan entrado varios. Encadenar cinco frases seguidas
      // no se escucha: se espera a que termine para poder hacer algo.
      const texto = nuevos.length === 1
        ? nuevos[0]!.texto
        : `${String(nuevos.length)} cosas nuevas. ${nuevos[nuevos.length - 1]!.texto}`;
      try { defaultTtsProvider.speak(texto); } catch { /* la campana ya sonó */ }
    }
  }, [data, prefs, voz.activa]);
}
